/**
 * Request Deduplicator
 * Prevents multiple simultaneous requests for the same data
 * Implements cache stampede protection
 */

class RequestDeduplicator {
  constructor() {
    this.pendingRequests = new Map(); // Active fetch promises
    this.requestQueue = new Map(); // Queued callbacks waiting for data
  }

  /**
   * Execute request with deduplication
   * Multiple simultaneous requests for same key will share the same promise
   */
  async execute(key, fetchFn) {
    // Check if there's already a pending request for this key
    if (this.pendingRequests.has(key)) {
      console.log(`♻️ Reusing pending request for: ${key}`);

      // Return the existing promise
      return this.pendingRequests.get(key);
    }

    // Create new fetch promise
    const fetchPromise = this.createFetchPromise(key, fetchFn);

    // Store the promise
    this.pendingRequests.set(key, fetchPromise);

    try {
      const result = await fetchPromise;
      return result;
    } finally {
      // Clean up after request completes
      this.pendingRequests.delete(key);
    }
  }

  /**
   * Create fetch promise with error handling
   */
  async createFetchPromise(key, fetchFn) {
    const startTime = Date.now();

    try {
      console.log(`🔄 Starting deduplicated fetch for: ${key}`);
      const result = await fetchFn();

      const duration = Date.now() - startTime;
      console.log(`✅ Deduplicated fetch completed: ${key} (${duration}ms)`);

      return result;
    } catch (error) {
      const duration = Date.now() - startTime;
      console.error(`❌ Deduplicated fetch failed: ${key} (${duration}ms)`, error);
      throw error;
    }
  }

  /**
   * Check if request is pending
   */
  isPending(key) {
    return this.pendingRequests.has(key);
  }

  /**
   * Get all pending request keys
   */
  getPendingKeys() {
    return Array.from(this.pendingRequests.keys());
  }

  /**
   * Cancel a pending request (if possible)
   */
  cancel(key) {
    if (this.pendingRequests.has(key)) {
      this.pendingRequests.delete(key);
      console.log(`🚫 Cancelled pending request: ${key}`);
      return true;
    }
    return false;
  }

  /**
   * Clear all pending requests
   */
  clearAll() {
    const count = this.pendingRequests.size;
    this.pendingRequests.clear();
    console.log(`🧹 Cleared ${count} pending requests`);
  }

  /**
   * Get deduplicator stats
   */
  getStats() {
    return {
      pendingRequests: this.pendingRequests.size,
      pendingKeys: this.getPendingKeys()
    };
  }
}

// Singleton instance
const requestDeduplicator = new RequestDeduplicator();

// Export for debugging
if (typeof window !== 'undefined') {
  window.RequestDedup = {
    stats: () => requestDeduplicator.getStats(),
    pending: () => requestDeduplicator.getPendingKeys(),
    clear: () => requestDeduplicator.clearAll()
  };
}

export default requestDeduplicator;